import { isParamTrue, isParamFalse } from './url';

/**
 * Generates a player url based a player configuration
 * to load the player in an iframe
 * @param {BambuserLiveShopping} embedInstance
 */
export const generatePlayerIframeUrlForEmbedInstance = (embedInstance, returnQueryString = false, overrides = {}) => {
  const {
    config: originalConfig,
    debug,
    isDesktop,
    isNonSupportedBrowser,
    logger,
    _trackingContext,
    _isAppDevEnv,
  } = embedInstance;
  const config = { ...originalConfig, ...overrides };
  const queryParams = [`type=${config.type}`];
  const accessibilityLabels = embedInstance._getPlayerAccessibilityLabels?.(config) || {};

  const pushTruthyParam = (name, value, encode = false, forcedValue) => {
    if (!value) return;
    if (forcedValue !== undefined) value = forcedValue;
    queryParams.push(`${name}=${encode ? encodeURIComponent(value) : value}`);
  };

  const allowSoundControl = isDesktop && (config.allowSoundControl || isParamTrue('allowSoundControl'));
  const merchantQueryParams = selectQueryParams(window.location.search, config.includeQueryParams);
  const setWithMinimizeSupport = config.hasOwnProperty('withMinimizeSupport');
  const { trimPriceTrailingZeros, checkoutOnCartClick, disableClickOutsideBehavior } = config;

  const isVOD = !!config.videoId;

  if (config.previewOrgId) {
    pushTruthyParam('previewOrgId', config.previewOrgId);
  }

  if (config.playerVersion) {
    pushTruthyParam('playerVersion', config.playerVersion);
  }
  if (config.isDashboard) {
    pushTruthyParam('isDashboard', config.isDashboard);
  }
  pushTruthyParam('allowFullscreen', config.allowFullscreen, false);
  pushTruthyParam('allowSoundControl', allowSoundControl);
  pushTruthyParam('allowShareAutoplay', config.allowShareAutoplay, false, 1);
  pushTruthyParam('addToCartEventOnly', config.addToCartEventOnly);
  pushTruthyParam('cartType', config.cartType);
  pushTruthyParam('chatName', config.chatName, true);
  // REMARK: VOD in the current form (at least in the case of carousel) is not supported with mini-player behaviour,
  // so opening in a new window is the only option. Technically focus-mode single should support this?
  pushTruthyParam('checkoutTarget', isVOD ? 'new-window' : config.checkoutTarget);
  pushTruthyParam('currency', config.currency);
  pushTruthyParam('debug', debug, true);
  pushTruthyParam('disableChatInput', config.disableChatInput, false);
  pushTruthyParam('deferReactAppInit', config.deferReactAppInit || isParamTrue('deferReactAppInit'));
  pushTruthyParam('channelId', config.channelId);
  pushTruthyParam('eventId', config.eventId || config.videoId);
  pushTruthyParam('originalEventId', config.originalEventId);
  pushTruthyParam('isVOD', isVOD); // This tells the player that it should treat the event as a VOD
  pushTruthyParam('landscapeInPortrait', config.landscapeInPortrait);
  pushTruthyParam('isVODStandalone', config.isVODStandalone);
  pushTruthyParam('hasAcceptedTerms', config.hasAcceptedTerms, false, 1);
  pushTruthyParam('hideActionBar', config.hideActionBar);
  pushTruthyParam('hideCartButton', config.hideCartButton);
  pushTruthyParam('hideCartView', config.hideCartView);
  pushTruthyParam('hideChatOverlay', config.hideChatOverlay);
  pushTruthyParam('hideEmojiOverlay', config.hideEmojiOverlay);
  // Overlay-mode specific override in cases where overlay shouldn't be closable via a player-provided button
  pushTruthyParam('hideCloseButton', config.hideCloseButton, false, 1);
  pushTruthyParam('hideProductList', config.hideProductList);
  pushTruthyParam('hideProductView', config.hideProductView);
  pushTruthyParam('hideShareButton', config.hideShareButton);
  pushTruthyParam('hideShareFromTimestampButton', config.hideShareFromTimestampButton);
  pushTruthyParam('hideShareView', config.hideShareView || config.hideShare);
  pushTruthyParam('hideWishlist', config.hideWishlist);
  pushTruthyParam('hidePlaybackRateButton', config.hidePlaybackRateButton);
  pushTruthyParam('hideClosedCaptionsButton', config.hideClosedCaptionsButton);
  pushTruthyParam('hideAudioLanguageButton', config.hideAudioLanguageButton);
  pushTruthyParam('hideVolumeButton', config.hideVolumeButton);
  pushTruthyParam('hidePromotedShows', config.hidePromotedShows);
  pushTruthyParam('innerTitle', accessibilityLabels.innerTitle, true);
  pushTruthyParam('playerOrientation', config.playerOrientation);

  // Remove once no client is using 'hideShare' anymore
  if (config.hideShare) {
    logger.warnProdDeprecated(`Player config 'hideShare' is deprecated. Please use 'hideShareView' instead`);
  }

  pushTruthyParam('hideAll', config.hideAll);
  pushTruthyParam('hideAddToCalendar', config.hideAddToCalendar);
  pushTruthyParam('hideAddToCalendarButton', config.hideAddToCalendarButton);
  pushTruthyParam('locale', config.locale);
  pushTruthyParam('merchantHref', window.location.href.replace(/\?.+/, ''), true);
  pushTruthyParam('merchantQueryParams', merchantQueryParams);
  pushTruthyParam('mockLiveBambuser', config.mockLive, true);
  pushTruthyParam('openProductsTarget', config.openProductsTarget);
  pushTruthyParam('playerContainerNode', config.playerContainerNode, true);
  pushTruthyParam('trimPriceTrailingZeros', trimPriceTrailingZeros || isParamTrue('trimPriceTrailingZeros'));
  pushTruthyParam('shareBaseUrl', config.shareBaseUrl, true);
  pushTruthyParam('shareTargets', config.shareTargets, true);
  pushTruthyParam('showShareButtonInMobileActionBar', config.showShareButtonInMobileActionBar);
  // sourceUrl is only used to detect instagram for escape (internal)
  pushTruthyParam('sourceUrl', config.sourceUrl || window.location.href, true);
  pushTruthyParam('withMinimizeSupport', setWithMinimizeSupport, false, config.withMinimizeSupport ? 1 : 0);
  pushTruthyParam('withoutProductDetails', config.withoutProductDetails, false, '1');
  pushTruthyParam('checkoutOnCartClick', checkoutOnCartClick);
  pushTruthyParam('startMuted', config.startMuted);
  pushTruthyParam('withGA', config.withGA);
  pushTruthyParam('withBaaSPlayerPreloading', config.withBaaSPlayerPreloading || isParamTrue('withBaaSPlayerPreloading'));
  // Signal that this player should automatically played when inititialized
  pushTruthyParam('autoplay', true, false, config.autoplay);
  pushTruthyParam('appDevEnv', _isAppDevEnv);
  pushTruthyParam('hideProducts', config.hideProducts);
  pushTruthyParam('distributionPageId', config.distributionPageId);
  pushTruthyParam('distributionContainerId', config.distributionContainerId);
  pushTruthyParam('placementId', config.placementId);
  pushTruthyParam('playlistId', config.playlistId);
  pushTruthyParam('appearanceGroupId', config.appearanceGroupId);
  pushTruthyParam('appearanceId', config.appearanceId);
  pushTruthyParam('audioTrackLocale', config.audioTrackLocale);
  pushTruthyParam('closeEventOnly', config.closeEventOnly);
  pushTruthyParam('minimizeEventOnly', config.minimizeEventOnly);
  pushTruthyParam('neverCollapseTimelineBarMobile', config.neverCollapseTimelineBarMobile);
  pushTruthyParam('openActionCard', config.openActionCard);
  pushTruthyParam('productListEventOnly', config.productListEventOnly);
  pushTruthyParam('disableTouchMoveScrollPrevention', config.disableTouchMoveScrollPrevention);
  if (config.allowMinimizeOnCurtains !== undefined) {
    queryParams.push(`allowMinimizeOnCurtains=${config.allowMinimizeOnCurtains ? 1 : 0}`);
  }
  // Explicit on/off toggles for social-media attribution chrome (TikTok).
  // Only forward when overridden — the iframe defaults to "show" when the
  // param is absent so the ToS-friendly defaults survive a missing config.
  if (config.showSocialMediaHandle !== undefined) {
    queryParams.push(`showSocialMediaHandle=${config.showSocialMediaHandle ? 1 : 0}`);
  }
  if (config.showSponsoredLabel !== undefined) {
    queryParams.push(`showSponsoredLabel=${config.showSponsoredLabel ? 1 : 0}`);
  }

  // If any safe area insets override is configured, pass them initially to the player
  if (typeof config.overrideSafeAreaInsets === 'object') {
    try {
      const safeAreaInsetsString = JSON.stringify(config.overrideSafeAreaInsets);
      pushTruthyParam('safeAreaInsets', encodeURIComponent(safeAreaInsetsString));
    } catch (error) {
      logger.error('Failed to stringify safe area insets', error);
    }
  }

  // REMARK: This is a preferable configuration for VOD type players.
  if (isVOD) {
    // show all products in the player timeline when the player is not associated with an event
    pushTruthyParam('autoShowAllProductsOnHighlightedArea', true);
  }

  let broadcastHash = config.broadcastHash;
  let timestamp = config.timestamp;
  if (config.deeplink && typeof config.deeplink === 'string') {
    const deeplinkParts = config.deeplink.split('@');
    if (deeplinkParts.length === 2 && !!deeplinkParts[0] && !!deeplinkParts[1]) {
      // We have exact 2 deeplink parts, the broadcast hash and the timestamp (hopefully...)
      broadcastHash = deeplinkParts[0];
      timestamp = deeplinkParts[1];
    }
  }

  pushTruthyParam('br', broadcastHash, true);
  pushTruthyParam('time', timestamp, true);
  pushTruthyParam('disableClickOutsideBehavior', disableClickOutsideBehavior);

  if (config.withClientSideProductData) {
    logger.warnProdDeprecated('Use of flag withClientSideProductData is deprecated, please use new product hydration');
    queryParams.push(`withClientSideProductData=${config.withClientSideProductData}`);
  }

  if (_trackingContext) {
    pushTruthyParam('uid', _trackingContext.userId);
    pushTruthyParam('sid', _trackingContext.sessionId);
  }

  if (config.themeId) {
    pushTruthyParam('themeId', config.themeId);
  }

  if (config.orgId) {
    pushTruthyParam('orgId', config.orgId);
  }

  if (config._useExternalPlayerController) {
    pushTruthyParam('useExternalPlayerController', config._useExternalPlayerController);

    // This config only makes sense when used in conjunction with _useExternalPlayerController, so
    // let's smash it into this conditional block to avoid it ever being pushed into the query string
    // if _useExternalPlayerController is not set to truthy value.
    if (config._preloadedExternalPlayer) {
      pushTruthyParam('preloadedExternalPlayer', config._preloadedExternalPlayer);
    }
  }

  if (config._disableWindowOpen) {
    pushTruthyParam('disableWindowOpen', config._disableWindowOpen);
  }

  if (config._sourceAppSuffix) {
    pushTruthyParam('sourceAppSuffix', config._sourceAppSuffix);
  }

  if (config._collectMetrics === false) {
    queryParams.push('_collectMetrics=false');
  }

  if (config.closedCaptions) {
    pushTruthyParam('closedCaptions', config.closedCaptions);
  }

  if (config.closedCaptionsLocaleHint) {
    pushTruthyParam('closedCaptionsLocaleHint', config.closedCaptionsLocaleHint);
  }

  if (isParamTrue('autoLoadPreviousRegistrySnapshot')) {
    pushTruthyParam('autoLoadPreviousRegistrySnapshot', true);
  }

  if (isParamTrue('_useMockPlayerController')) {
    pushTruthyParam('_useMockPlayerController', true);
  }

  if (isParamTrue('useNewImageTransformer')) {
    pushTruthyParam('useNewImageTransformer', true);
  }

  if (isParamTrue('bypassThemeCache')) {
    pushTruthyParam('bypassThemeCache', true);
  }

  if (!isParamFalse('withStatePreloading')) {
    pushTruthyParam('withStatePreloading', true);
  }

  if (!isParamFalse('withChatMessageBackend')) {
    pushTruthyParam('withChatMessageBackend', true);
  }

  if (!isParamFalse('withReactionsBackend')) {
    pushTruthyParam('withReactionsBackend', true);
  }

  if (!isParamFalse('withProductsBackend')) {
    pushTruthyParam('withProductsBackend', true);
  }

  if (!isParamFalse('allowChatBlur')) {
    pushTruthyParam('allowChatBlur', true);
  }

  if (isParamTrue('forceFocus')) {
    pushTruthyParam('forceFocus', true);
  }

  // Pass along the flag to enable server timing from the CDN, which
  // can be used to get insights into CDN performance and potentially
  // used for debugging and optimization purposes.
  if (isParamTrue('bam_cdn_server_timing')) {
    // It's important that we set this to 1 or 0, and not true or false,
    // as that's what's expected by the core player lib.
    queryParams.push('bam_cdn_server_timing=1');
  }

  if (Array.isArray(config.eventHandlers) && config.eventHandlers.length > 0) {
    pushTruthyParam('eventHandlers', JSON.stringify(config.eventHandlers), true);
  }

  pushTruthyParam('webComponentTagName', config.webComponentTagName);

  if (returnQueryString) {
    return `${queryParams.join('&')}`;
  }

  let iframeUrl = config.playerUrl;
  if (iframeUrl.startsWith('//')) iframeUrl = `${window.location.protocol}${iframeUrl}`;

  if (!iframeUrl.endsWith('/')) iframeUrl += '/'; // End base url with a dash to avoid redundant redirections by CDN
  if (isNonSupportedBrowser) iframeUrl += 'unsupported-browser.html';
  if (queryParams.length > 0) iframeUrl += `#${queryParams.join('&')}`;
  logger.log('_getPlayerUrl() url', iframeUrl, 'config', config);

  return iframeUrl;
};

/**
 * Selects query parameters from a list of query parameters to be included
 * in the query params exposed to the Player
 * @param {string} queryParams - all query parameters as a query string; `?foo=bar&baz=bat`
 * @param {string[]} includeQueryParams
 * @return {string} selected query parameters
 */
export const selectQueryParams = (queryParams, includeQueryParams = []) => {
  if (!queryParams) {
    return '';
  }

  const qps = new URLSearchParams(queryParams);
  const selected = new URLSearchParams();

  for (const k of includeQueryParams) {
    const v = qps.get(k);
    if (v !== null) {
      selected.append(k, v);
    }
  }

  return encodeURIComponent(selected);
};
